(function () {
  // Cart meenemen bij een market-switch naar een ander domein.
  //
  // De market switcher ({% form 'localization' %}) post naar /localization,
  // waarna Shopify doorstuurt naar het domein van de gekozen markt (.com ->
  // .nl). Het cart-cookie is domeingebonden: het cart-token van .com is op .nl
  // niet leesbaar, en Shopify heeft geen endpoint om een token van een ander
  // domein over te nemen. De cart wordt daarom op het nieuwe domein opnieuw
  // opgebouwd: bij het switchen hangen we een compacte beschrijving van de cart
  // (variant-ID's, aantallen, properties, selling plans, note en attributes)
  // aan return_to, en op het nieuwe domein zet dit script die regels terug via
  // /cart/add.js. Zelfde shop = zelfde variant-ID's, en de prijzen worden
  // automatisch in de valuta van de nieuwe markt gezet.
  //
  // Is de bron-cart leeg, dan gebeurt er niets en houdt het nieuwe domein
  // gewoon zijn eigen cart.

  const PARAM = 'cart_transfer';
  const VERSION = 1;
  const MAX_PARAM_LENGTH = 1600; // ruim binnen de veilige URL-lengte
  const MAX_AGE_MS = 10 * 60 * 1000; // oude of gedeelde links niet toepassen
  const CART_FETCH_TIMEOUT = 2000; // de switch mag hier nooit op blijven hangen

  // Taalwissels blijven binnen hetzelfde marktdomein, dus daar is overdracht
  // niet nodig. Zet op true als taal in deze shop wél van domein wisselt.
  const TRANSFER_ON_LANGUAGE_CHANGE = false;

  let submitting = false;

  /* ---------------------------------------------------------------- routes */

  const route = (name, fallback) => window.theme?.routes?.[name] || fallback;

  const cartJsonUrl = () => `${route('cart_url', '/cart')}.js`;
  const cartAddUrl = () => `${route('cart_add_url', '/cart/add')}.js`;
  const cartUpdateUrl = () => `${route('cart_update_url', '/cart/update')}.js`;
  const cartClearUrl = () => `${route('cart_url', '/cart')}/clear.js`;

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  async function fetchCart(timeout = 0) {
    const options = { headers: { 'Accept': 'application/json' } };

    if (timeout) {
      const controller = new AbortController();
      options.signal = controller.signal;
      setTimeout(() => controller.abort(), timeout);
    }

    try {
      const response = await fetch(cartJsonUrl(), options);
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  /* -------------------------------------------------------------- encoding */

  function encodePayload(payload) {
    const bytes = new TextEncoder().encode(JSON.stringify(payload));
    const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function decodePayload(value) {
    const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes));
  }

  /* ------------------------------------------------------- cart <-> payload */

  function lineProperties(properties) {
    const entries = Object.entries(properties || {}).filter(([, value]) => value !== null && value !== '');

    return entries.length > 0 ? Object.fromEntries(entries) : null;
  }

  function toLine(item) {
    const line = { id: item.id, q: item.quantity };
    const properties = lineProperties(item.properties);
    const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;

    if (properties) line.p = properties;
    if (sellingPlan) line.s = sellingPlan;

    return line;
  }

  function buildPayload(cart) {
    if (!cart?.items?.length) return null;

    const payload = { v: VERSION, t: Date.now(), i: cart.items.map(toLine) };

    if (cart.note) payload.n = cart.note;
    if (Object.keys(cart.attributes || {}).length > 0) payload.a = cart.attributes;

    return payload;
  }

  // Valt stap voor stap terug op minder data zodat de URL nooit te lang wordt.
  function encodeWithinLimit(payload) {
    let encoded = encodePayload(payload);
    if (encoded.length <= MAX_PARAM_LENGTH) return encoded;

    const stripped = { v: payload.v, t: payload.t, i: payload.i };
    encoded = encodePayload(stripped);
    if (encoded.length <= MAX_PARAM_LENGTH) return encoded;

    stripped.i = stripped.i.map(({ id, q, s }) => (s ? { id, q, s } : { id, q }));
    encoded = encodePayload(stripped);
    if (encoded.length <= MAX_PARAM_LENGTH) return encoded;

    while (stripped.i.length > 1) {
      stripped.i = stripped.i.slice(0, -1);
      encoded = encodePayload(stripped);
      if (encoded.length <= MAX_PARAM_LENGTH) return encoded;
    }

    return null;
  }

  /* ------------------------------------------------------- market switcher */

  // Zelfde return_to als het thema zelf gebruikt (pad zonder query), met alleen
  // de cart-parameter erachter.
  const returnToPath = (encoded) => `${window.location.pathname}?${PARAM}=${encoded}`;

  async function submitForm(form) {
    if (submitting) return;
    submitting = true;

    form.setAttribute('aria-busy', 'true');

    try {
      const payload = buildPayload(await fetchCart(CART_FETCH_TIMEOUT));
      const encoded = payload && encodeWithinLimit(payload);
      const returnTo = form.querySelector('input[name="return_to"]');

      // Geen (bruikbare) cart of geen return_to-veld: gewoon switchen.
      if (encoded && returnTo) returnTo.value = returnToPath(encoded);
    } catch (error) {
      console.log('cart-market-transfer:', error);
    }

    form.removeAttribute('aria-busy');
    HTMLFormElement.prototype.submit.call(form);
  }

  function isCountryForm(form) {
    return TRANSFER_ON_LANGUAGE_CHANGE || form.querySelector('[name="country_code"]') !== null;
  }

  // De theme-componenten roepen form.submit() rechtstreeks aan (er is dus geen
  // submit-event), dus onderscheppen we in de capture-fase vóór hun handler.
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[data-value]');
    const form = link?.closest('form[is="localization-listbox"]');
    if (!form || !isCountryForm(form)) return;

    const input = form.querySelector('input[name="country_code"], input[name="locale_code"]');
    const value = link.getAttribute('data-value');
    if (!input || !value || value === input.value) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    input.value = value;

    const returnTo = form.querySelector('input[name="return_to"]');
    if (returnTo) returnTo.value = window.location.pathname;

    submitForm(form);
  }, true);

  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('select[name="country_code"], select[name="locale_code"]')) return;

    const form = event.target.closest('form[is="localization-form"]');
    if (!form || !isCountryForm(form)) return;

    event.stopImmediatePropagation();

    submitForm(form);
  }, true);

  /* ------------------------------------------------ herstel op nieuw domein */

  function sameContents(cart, payload) {
    const items = cart?.items || [];

    return items.length === payload.i.length
      && payload.i.every((line, index) => items[index].id === line.id && items[index].quantity === line.q);
  }

  async function addLines(lines) {
    const items = lines.map((line) => {
      const item = { id: line.id, quantity: line.q };

      if (line.p) item.properties = line.p;
      if (line.s) item.selling_plan = line.s;

      return item;
    });

    try {
      const response = await postJSON(cartAddUrl(), { items });
      if (response.ok) return items.length;
    } catch (error) {
      return 0;
    }

    // /cart/add.js is atomair: één regel die in deze markt niet bestaat laat de
    // hele batch falen. Daarom regel voor regel opnieuw, de rest komt wel binnen.
    let added = 0;

    for (const item of items) {
      try {
        const response = await postJSON(cartAddUrl(), { items: [item] });
        if (response.ok) added++;
      } catch (error) {
        // Niet beschikbaar in deze markt, overslaan.
      }
    }

    return added;
  }

  async function notifyTheme() {
    const cart = await fetchCart();

    if (cart && window.theme?.pubsub) {
      window.theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-market-transfer', cart });
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));

    // De cart-pagina is server-side gerenderd met de oude (lege) cart.
    if (window.theme?.settings?.isCartTemplate || document.body.classList.contains('template-cart')) {
      window.location.reload();
    }
  }

  async function restore() {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(PARAM);
    if (!raw) return;

    // Meteen uit de URL halen: niet nog eens toepassen bij refresh of delen.
    url.searchParams.delete(PARAM);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    let payload;
    try {
      payload = decodePayload(raw);
    } catch (error) {
      return;
    }

    if (payload?.v !== VERSION || !Array.isArray(payload.i) || payload.i.length === 0) return;
    if (!payload.t || Date.now() - payload.t > MAX_AGE_MS) return;

    const cart = await fetchCart();
    if (!cart) return;

    // Zelfde domein: Shopify hield het cart-cookie vast, niets te doen.
    if (sameContents(cart, payload)) return;

    const previous = cart.items || [];

    if (previous.length > 0) {
      try {
        await postJSON(cartClearUrl(), {});
      } catch (error) {
        return;
      }
    }

    const added = await addLines(payload.i);

    if (added === 0) {
      // Niets van de meegenomen cart bestaat in deze markt: cart terugzetten.
      if (previous.length > 0) {
        await addLines(previous.map(toLine));
        await notifyTheme();
      }
      return;
    }

    const update = {};
    if (payload.n) update.note = payload.n;
    if (payload.a) update.attributes = payload.a;

    if (Object.keys(update).length > 0) {
      try {
        await postJSON(cartUpdateUrl(), update);
      } catch (error) {
        // Note/attributes zijn bijzaak, de regels staan er.
      }
    }

    await notifyTheme();
  }

  restore().catch((error) => console.log('cart-market-transfer:', error));
})();
